/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const CREATE_AGENT_AUTHORIZATION_DISCRIMINATOR = new Uint8Array([
  48, 219, 232, 202, 151, 97, 230, 20,
]);

export function getCreateAgentAuthorizationDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREATE_AGENT_AUTHORIZATION_DISCRIMINATOR,
  );
}

export type CreateAgentAuthorizationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthorization extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthorization extends string
        ? WritableAccount<TAccountAuthorization>
        : TAccountAuthorization,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CreateAgentAuthorizationInstructionData = {
  discriminator: ReadonlyUint8Array;
  authorizedSource: Address;
  indexLimit: bigint;
  expiresAt: bigint;
  network: number;
  signature: ReadonlyUint8Array;
  nonce: Option<string>;
};

export type CreateAgentAuthorizationInstructionDataArgs = {
  authorizedSource: Address;
  indexLimit: number | bigint;
  expiresAt: number | bigint;
  network: number;
  signature: ReadonlyUint8Array;
  nonce: OptionOrNullable<string>;
};

export function getCreateAgentAuthorizationInstructionDataEncoder(): Encoder<CreateAgentAuthorizationInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["authorizedSource", getAddressEncoder()],
      ["indexLimit", getU64Encoder()],
      ["expiresAt", getI64Encoder()],
      ["network", getU8Encoder()],
      ["signature", fixEncoderSize(getBytesEncoder(), 64)],
      [
        "nonce",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: CREATE_AGENT_AUTHORIZATION_DISCRIMINATOR,
    }),
  );
}

export function getCreateAgentAuthorizationInstructionDataDecoder(): Decoder<CreateAgentAuthorizationInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["authorizedSource", getAddressDecoder()],
    ["indexLimit", getU64Decoder()],
    ["expiresAt", getI64Decoder()],
    ["network", getU8Decoder()],
    ["signature", fixDecoderSize(getBytesDecoder(), 64)],
    [
      "nonce",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getCreateAgentAuthorizationInstructionDataCodec(): Codec<
  CreateAgentAuthorizationInstructionDataArgs,
  CreateAgentAuthorizationInstructionData
> {
  return combineCodec(
    getCreateAgentAuthorizationInstructionDataEncoder(),
    getCreateAgentAuthorizationInstructionDataDecoder(),
  );
}

export type CreateAgentAuthorizationAsyncInput<
  TAccountAgent extends string = string,
  TAccountAuthorization extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  /** Agent granting authorization */
  agent: Address<TAccountAgent>;
  /** Authorization account (PDA) */
  authorization?: Address<TAccountAuthorization>;
  /** Authority (agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  /** System program */
  systemProgram?: Address<TAccountSystemProgram>;
  authorizedSource: CreateAgentAuthorizationInstructionDataArgs["authorizedSource"];
  indexLimit: CreateAgentAuthorizationInstructionDataArgs["indexLimit"];
  expiresAt: CreateAgentAuthorizationInstructionDataArgs["expiresAt"];
  network: CreateAgentAuthorizationInstructionDataArgs["network"];
  signature: CreateAgentAuthorizationInstructionDataArgs["signature"];
  nonce: CreateAgentAuthorizationInstructionDataArgs["nonce"];
};

export async function getCreateAgentAuthorizationInstructionAsync<
  TAccountAgent extends string,
  TAccountAuthorization extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateAgentAuthorizationAsyncInput<
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CreateAgentAuthorizationInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agent: { value: input.agent ?? null, isWritable: false },
    authorization: { value: input.authorization ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.authorization.value) {
    accounts.authorization.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([97, 103, 101, 110, 116, 95, 97, 117, 116, 104]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
        getAddressEncoder().encode(expectSome(args.authorizedSource)),
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ).encode(expectSome(args.nonce)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authorization),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateAgentAuthorizationInstructionDataEncoder().encode(
      args as CreateAgentAuthorizationInstructionDataArgs,
    ),
    programAddress,
  } as CreateAgentAuthorizationInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type CreateAgentAuthorizationInput<
  TAccountAgent extends string = string,
  TAccountAuthorization extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  /** Agent granting authorization */
  agent: Address<TAccountAgent>;
  /** Authorization account (PDA) */
  authorization: Address<TAccountAuthorization>;
  /** Authority (agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  /** System program */
  systemProgram?: Address<TAccountSystemProgram>;
  authorizedSource: CreateAgentAuthorizationInstructionDataArgs["authorizedSource"];
  indexLimit: CreateAgentAuthorizationInstructionDataArgs["indexLimit"];
  expiresAt: CreateAgentAuthorizationInstructionDataArgs["expiresAt"];
  network: CreateAgentAuthorizationInstructionDataArgs["network"];
  signature: CreateAgentAuthorizationInstructionDataArgs["signature"];
  nonce: CreateAgentAuthorizationInstructionDataArgs["nonce"];
};

export function getCreateAgentAuthorizationInstruction<
  TAccountAgent extends string,
  TAccountAuthorization extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateAgentAuthorizationInput<
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CreateAgentAuthorizationInstruction<
  TProgramAddress,
  TAccountAgent,
  TAccountAuthorization,
  TAccountAuthority,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agent: { value: input.agent ?? null, isWritable: false },
    authorization: { value: input.authorization ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authorization),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateAgentAuthorizationInstructionDataEncoder().encode(
      args as CreateAgentAuthorizationInstructionDataArgs,
    ),
    programAddress,
  } as CreateAgentAuthorizationInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type ParsedCreateAgentAuthorizationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Agent granting authorization */
    agent: TAccountMetas[0];
    /** Authorization account (PDA) */
    authorization: TAccountMetas[1];
    /** Authority (agent owner) */
    authority: TAccountMetas[2];
    /** System program */
    systemProgram: TAccountMetas[3];
  };
  data: CreateAgentAuthorizationInstructionData;
};

export function parseCreateAgentAuthorizationInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCreateAgentAuthorizationInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agent: getNextAccount(),
      authorization: getNextAccount(),
      authority: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getCreateAgentAuthorizationInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
